/** Generate Patent Draft as the server owns it: the review, the latest run and its download. */

import type {
  DraftSurveyResponse,
  GenerateReviewResponse,
  GenerateRunResponse,
  RunDownloadResponse,
  ShownTerms,
} from "@patent-studio/api-types";
import { ApiError, get, post } from "@/services/api-client";

/** Everything the run would read, card by card, and whether it may start. */
export function readGenerateReview(
  taskId: string,
): Promise<GenerateReviewResponse> {
  return get<GenerateReviewResponse>(`/tasks/${taskId}/generate/review`);
}

/**
 * Start a draft run over the inputs as they now stand, naming the open items
 * the attorney generates over. The server freezes the inputs and checks every
 * gate on that one state; it answers 409 with the gate, a changed list of open
 * items, or a run already under way.
 */
export function startRun(
  taskId: string,
  openItemsAddress: string,
  shownTerms: ShownTerms | null = null,
): Promise<GenerateRunResponse> {
  return post<GenerateRunResponse>(`/tasks/${taskId}/generate`, {
    open_items_address: openItemsAddress,
    // The terms the review showed, which the run's lock counts edits against.
    ...(shownTerms === null ? {} : { shown_terms: shownTerms }),
  });
}

/**
 * The task's draft run queued last, or null while it has none. The server
 * answers 404 for a task with no run; the draft's own shell has already
 * settled that the task is the caller's, so here a 404 means no run.
 */
export async function readLatestRun(
  taskId: string,
): Promise<GenerateRunResponse | null> {
  try {
    return await get<GenerateRunResponse>(
      `/tasks/${taskId}/generate/runs/latest`,
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/**
 * A signed link to a ready run's bundle: the draft and its two records in one
 * zip. The server answers 409 until the run is ready and holds its bundle.
 */
export function readRunDownload(
  taskId: string,
  runId: string,
): Promise<RunDownloadResponse> {
  return get<RunDownloadResponse>(
    `/tasks/${taskId}/generate/runs/${runId}/download`,
  );
}

/**
 * The finished draft's survey as the server offers it to the caller: the link
 * while it is unanswered, or null once it is answered and wherever there is no
 * survey to offer (staff, a draft not finished, a stage with the survey off).
 */
export function readDraftSurvey(taskId: string): Promise<DraftSurveyResponse> {
  return get<DraftSurveyResponse>(`/tasks/${taskId}/survey`);
}

/** Hand a signed link to the browser to save, as the working draft's export does. */
export function startDownload(url: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
